import React, { useEffect } from 'react'
import classNames from 'classnames'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { Form, Field, WatchField, useForm, isEmpty } from '../../../../form'
import MenuItem from '@material-ui/core/MenuItem'
import { Translation, useTranslation } from 'react-i18next'
import { withStyles } from '@material-ui/core/styles'
import {
  getInvoiceUserEmail,
  getUserCompanyUpdateInfo
} from '../../../../actions'
import { invoiceDeliveryAndDetailsValidate } from '../../../../containers/validate'
import { createMenuItems } from '../../../../utils/invoice.utils'
import Button from '@material-ui/core/Button'
import mobileView from '../../../../utils/mobile.utils'

const styles = (theme) => ({
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  resize: {
    fontSize: '14px'
  },
  checkbox: {
    fontSize: '14px',
    fontWeight: 'bold',
    color: '#808080',
    marginBottom: '10px'
  },
  button: {
    cursor: 'pointer',
    fontWeight: '600',
    fontSize: '15px',
    borderRadius: '10px'
  },
  buttonBack: {
    padding: !mobileView ? '2px 30px' : '4px 20px',
    color: 'rgb(83 77 77)',
    backgroundColor: '#fff',
    border: '2px solid #44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#E0F7FA'
    }
  },
  buttonNext: {
    padding: !mobileView ? '0 25px' : '2px 20px',
    color: '#fff',
    backgroundColor: '#44C0CC',
    borderRadius: mobileView ? '4px' : '',
    [theme.breakpoints.down('375')]: {
      padding: '1px 12px'
    },
    '&:hover': {
      backgroundColor: '#3BA9B5'
    }
  }
})

const InvoiceDeliveryForm = ({ classes, goBack, goNext, formValues }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const { invoiceOperators } = useSelector(
    (state) => state.invoice,
    shallowEqual
  )

  const _invoiceOperators = ['', ...invoiceOperators]

  const form = useForm({
    name: 'invoice',
    onSubmit: onSubmit,
    defaultValues: formValues,
    validator: (values) => invoiceDeliveryAndDetailsValidate(values),
    keepEmptyValuesOnSubmit: false
  })

  useEffect(() => {
    form.reset(formValues)
  }, [formValues])

  const {
    control,
    formState: { errors }
  } = form

  const [attemptingSubmit, setAttemptingSubmit] = React.useState(false)

  function onSubmit(values) {
    goNext(values)
  }

  function clearErrors(fields) {
    if (fields) {
      form?.clearErrors(fields)
    } else {
      form?.clearErrors()
    }
  }

  React.useEffect(() => {
    if (attemptingSubmit && !isEmpty(errors)) {
      setAttemptingSubmit(false)
    }
  }, [attemptingSubmit, errors])

  React.useEffect(() => {
    dispatch(getInvoiceUserEmail(0))
    dispatch(getUserCompanyUpdateInfo())
  }, [])

  return (
    <Form
      {...form}
      style={{ display: 'flex', height: '100%', flexDirection: 'column' }}
    >
      <div
        style={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center'
        }}
      >
        <div className="row" style={{ padding: mobileView ? '0 20px' : '' }}>
          <div className="col-xs-12 col-sm-4 col-lg-4">
            <InvoiceDeliveryMethod
              classes={classes}
              invoiceOperators={_invoiceOperators}
              clearErrors={clearErrors}
              control={control}
            />
          </div>
        </div>
      </div>
      <div
        style={{
          display: 'flex',
          gap: '15px',
          justifyContent: mobileView ? 'center' : 'flex-end',
          margin: '40px 10px 15px'
        }}
      >
        <Button
          className={classNames(classes.buttonBack, classes.button)}
          onClick={() => goBack(form.getValues())}
        >
          {t('asiakkaat.back')}
        </Button>
        <Button
          className={classNames(classes.buttonNext, classes.button)}
          type="submit"
          onClick={() => setAttemptingSubmit(true)}
        >
          {t('asiakkaat.next')}
          <span
            style={{
              display: 'inline-block',
              transform: 'scaleX(1.5)',
              fontSize: '18px',
              letterSpacing: '-2px'
            }}
          >
            &nbsp; &gt;
          </span>
        </Button>
      </div>
    </Form>
  )
}

export const InvoiceDeliveryMethod = ({
  classes,
  invoiceOperators,
  clearErrors,
  control
}) => {
  return (
    <Translation>
      {(t) => {
        return (
          <div className="panel panel-default">
            <h3
              style={{
                textAlign: !mobileView ? 'left' : 'center',
                fontSize: mobileView && '20px',
                fontWeight: '600',
                color: '#000',
                textTransform: 'none'
              }}
            >
              {t('invoice.panelh3b')}
            </h3>
            <div>
              <Field
                name="delivery_method"
                inputType="select"
                label={t('invoice.laskun')}
                className={classes.select}
                onChange={(e) => {
                  if (e.target.value === 'Sähköposti') {
                    clearErrors([
                      'delivery_address',
                      'zip_code',
                      'city',
                      'web_invoice',
                      'finvoice_operator'
                    ])
                  } else if (e.target.value === 'Kirjeposti') {
                    clearErrors(['web_invoice', 'finvoice_operator'])
                  }
                }}
                control={control}
              >
                <MenuItem value="Sähköposti">{t('Sähköposti')}</MenuItem>
                <MenuItem value="Kirjeposti">{t('Kirjeposti')}</MenuItem>
                <MenuItem value="Verkkolasku">{t('Verkkolasku')}</MenuItem>
              </Field>
            </div>
            <div>
              <WatchField control={control} fields="delivery_method">
                {(watch) => (
                  <Field
                    name="delivery_address"
                    inputType="textfield"
                    label={
                      watch.delivery_method !== 'Kirjeposti' &&
                      watch.delivery_method !== 'Verkkolasku'
                        ? t('invoice.label6').replace(' *', '')
                        : t('invoice.label6')
                    }
                    className={classNames(classes.textField, classes.resize)}
                    control={control}
                  />
                )}
              </WatchField>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <div style={{ width: '46%' }}>
                <WatchField control={control} fields="delivery_method">
                  {(watch) => (
                    <Field
                      name="zip_code"
                      inputType="textfield"
                      label={
                        watch.delivery_method !== 'Kirjeposti' &&
                        watch.delivery_method !== 'Verkkolasku'
                          ? t('invoice.posti').replace(' *', '')
                          : t('invoice.posti')
                      }
                      className={classNames(classes.textField, classes.resize)}
                      control={control}
                    />
                  )}
                </WatchField>
              </div>
              <div style={{ width: '46%', whiteSpace: 'nowrap' }}>
                <WatchField control={control} fields="delivery_method">
                  {(watch) => (
                    <Field
                      name="city"
                      inputType="textfield"
                      label={
                        watch.delivery_method !== 'Kirjeposti' &&
                        watch.delivery_method !== 'Verkkolasku'
                          ? t('invoice.posti2').replace(' *', '')
                          : t('invoice.posti2')
                      }
                      className={classNames(classes.textField, classes.resize)}
                      control={control}
                    />
                  )}
                </WatchField>
              </div>
            </div>
            <div>
              <WatchField fields={'delivery_method'} control={control}>
                {(watch) => (
                  <Field
                    name="web_invoice"
                    inputType="textfield"
                    label={
                      watch.delivery_method !== 'Sähköposti' &&
                      watch.delivery_method !== 'Kirjeposti'
                        ? t('invoice.verkko') + '*'
                        : t('invoice.verkko')
                    }
                    className={classNames(classes.textField, classes.resize)}
                    disabled={watch.delivery_method === 'Kirjeposti'}
                    displayValue={
                      watch.delivery_method === 'Kirjeposti'
                        ? 'TULOSTUS'
                        : undefined
                    }
                    control={control}
                  />
                )}
              </WatchField>
            </div>
            <div>
              <WatchField fields={'delivery_method'} control={control}>
                {(watch) => (
                  <Field
                    name="finvoice_operator"
                    inputType="select"
                    label={
                      watch.delivery_method !== 'Sähköposti' &&
                      watch.delivery_method !== 'Kirjeposti'
                        ? t('invoice.verkko2') + '*'
                        : t('invoice.verkko2')
                    }
                    className={classes.select}
                    control={control}
                  >
                    {createMenuItems(invoiceOperators)}
                  </Field>
                )}
              </WatchField>
            </div>
          </div>
        )
      }}
    </Translation>
  )
}

export default withStyles(styles)(InvoiceDeliveryForm)
